<template>
  <div>
    <FocusIndicator
      card-selector=".b-table:not(.gid-table-infinite) tbody tr"
      :name-extractor="extractBrandName"
    />
    <b-card bg-variant="light" class="my-3">
      <b-row>
        <b-col md="4">
          <b-form-group label-cols="2" :label="`${$t('filter')}:`" class="mb-0">
            <b-input-group>
              <b-form-input
                v-model="filterSearch"
                :placeholder="$t('type-to-search')"
                debounce="300"
              />
              <b-input-group-append v-if="filterSearch">
                <b-btn @click="filterSearch = ''">&times;</b-btn>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label-cols="2" :label="`${$t('status')}:`" class="mb-0">
            <div class="d-flex align-items-stretch">
              <multiselect
                v-model="selectedStatuses"
                :options="accountStatusOptions"
                :multiple="true"
                label="text"
                track-by="value"
                :placeholder="$t('brand.list.select-statuses')"
                class="flex-grow-1"
                @input="onStatusChange"
              />
              <b-button-group class="ml-2 status-button-group">
                <b-button
                  :variant="
                    isActiveStatusesSelected ? 'primary' : 'outline-primary'
                  "
                  @click="selectActiveStatuses"
                  :title="$t('brand.list.select-active')"
                  class="status-button"
                >
                  <i class="material-icons">check_circle</i>
                </b-button>
                <b-button
                  variant="outline-secondary"
                  @click="clearAllStatuses"
                  :title="$t('brand.list.clear-statuses')"
                  class="status-button"
                >
                  <i class="material-icons">clear</i>
                </b-button>
              </b-button-group>
            </div>
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label-cols="3" :label="`${$t('owner')}:`" class="mb-0">
            <b-form-select v-model="filterOwner" :options="owners">
              <template slot="first">
                <option :value="null">-- {{ $t('all') }} --</option>
              </template>
            </b-form-select>
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>
    <b-pagination
      v-if="totalRows > perPage"
      :total-rows="totalRows"
      :per-page="perPage"
      v-model="currentPage"
      align="center"
    />
    <b-table
      show-empty
      striped
      stacked="md"
      :items="items"
      :busy="loading"
      :fields="tableFields"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDir"
      no-local-sorting
    >
      <template #thead-top>
        <tr>
          <th colspan="2">{{ $t('brand.list.header-brand') }}</th>
          <th colspan="3" class="border-left">
            {{ $t('brand.list.header-details') }}
          </th>
          <th>&nbsp;</th>
        </tr>
      </template>
      <template #cell(name)="{ item }">
        <p>
          <span
            role="button"
            class="clickable-text"
            :title="$t('brand.list.click-hint')"
            @click="handleBrandClick(item)"
          >
            {{ item.name }}
          </span>
          <br />
          <template v-if="item.shippingAddress">
            <span
              class="clickable-text address-line"
              role="button"
              @click="copyToClipboard(item.shippingAddress.street)"
            >
              {{ item.shippingAddress.street }}
            </span>
            <br />
            <span
              class="clickable-text address-line"
              role="button"
              @click="
                copyToClipboard(
                  `${item.shippingAddress.countryCode} - ${item.shippingAddress.postalCode} ${item.shippingAddress.city}`,
                )
              "
            >
              {{ item.shippingAddress.countryCode }} -
              {{ item.shippingAddress.postalCode }}
              {{ item.shippingAddress.city }}
            </span>
          </template>
          <br />
          <template v-if="item.website">
            <a :href="websiteURL(item.website)" target="_blank">
              {{ item.website }}
            </a>
          </template>
        </p>
      </template>
      <template #cell(phone)="{ item }">
        <span
          v-if="item.phone"
          role="button"
          class="clickable-text"
          @click="handleActionClick(item.phone, `tel:${item.phone}`)"
        >
          {{ item.phone }}
        </span>
      </template>
      <template #cell(contacts)="{ item }">
        <div v-if="item.contacts && item.contacts.length" class="contacts-cell">
          <div
            v-for="contact in item.contacts"
            :key="contact.id"
            class="contact-row"
          >
            <span
              role="button"
              class="contact-name clickable-text"
              :title="$t('brand.list.click-hint')"
              @click="handleContactClick(contact)"
            >
              {{ contact.firstName }} {{ contact.lastName }}
            </span>
            <span class="contact-actions">
              <span
                v-if="contact.email"
                role="button"
                :title="contact.email"
                class="contact-action"
                @click="
                  handleActionClick(contact.email, `mailto:${contact.email}`)
                "
              >
                <i class="material-icons">email</i>
              </span>
              <span
                v-if="contact.phone"
                role="button"
                :title="contact.phone"
                class="contact-action"
                @click="
                  handleActionClick(contact.phone, `tel:${contact.phone}`)
                "
              >
                <i class="material-icons">phone</i>
              </span>
              <span
                v-if="contact.mobilePhone"
                role="button"
                :title="contact.mobilePhone"
                class="contact-action"
                @click="
                  handleActionClick(
                    contact.mobilePhone,
                    `tel:${contact.mobilePhone}`,
                  )
                "
              >
                <i class="material-icons">smartphone</i>
              </span>
            </span>
          </div>
        </div>
        <span v-else class="text-muted">-</span>
      </template>
      <template #cell(status)="{ item }">
        <b-form-select
          :value="item.status"
          :options="statusOptions"
          size="sm"
          class="status-select"
          :class="`status-${statusVariant(item.status)}`"
          @change="updateStatus(item, $event)"
        />
      </template>
      <template #cell(owner)="{ item }">
        <b-form-select
          :value="item.ownerid"
          :options="ownerOptions"
          size="sm"
          class="owner-select"
          @change="updateOwner(item, $event)"
        >
          <template slot="first">
            <option :value="null">-</option>
          </template>
        </b-form-select>
      </template>
      <template
        #cell(_details)="{ toggleDetails, detailsShowing }"
        colspan="100%"
      >
        <button
          @click.stop="toggleDetails"
          style="font-size: 1.9rem; background: transparent; border: 0"
          class="material-icons"
        >
          {{ detailsShowing ? 'expand_less' : 'expand_more' }}
        </button>
      </template>
      <template #row-details="{ item }">
        <brand-details :brand="item" @update="loadItems" />
      </template>
    </b-table>
    <b-alert v-if="error !== null" variant="danger" class="text-left" show>
      <h4 class="alert-heading">{{ $t('_errors.network.title') }}</h4>
      <p>{{ error.message }}</p>
      <b-collapse id="error-details" class="my-2">
        <b-card>
          <pre>{{ error.details }}</pre>
        </b-card>
      </b-collapse>
      <b-btn v-b-toggle.error-details>
        {{ $t('login.buttons.error-details') }}
      </b-btn>
    </b-alert>
    <b-pagination
      :total-rows="totalRows"
      :per-page="perPage"
      v-model="currentPage"
      align="center"
    />
    <div v-if="currentPageCount" class="text-center text-muted">
      <small>{{ currentPageCount }} / {{ totalRows }}</small>
    </div>
  </div>
</template>

<script>
import { SET_BRANDS_FILTER, SET_BRANDS_DATA } from '@/store/brands.module';
import { CondOperator, RequestQueryBuilder } from '@dataui/crud-request';
import { AccountStatusEnum, ContactProfileEnum } from '@gid/models';
import FocusIndicator from '@gid/vue-common/components/FocusIndicator.vue';
import { mapFilters } from '@gid/vue-common/store/utils';
import { normalizeSearchInput } from '@gid/vue-common/utils';
import { mapMutations } from 'vuex';
import axios from 'axios';
import BrandDetails from './BrandDetails.vue';

export default {
  components: {
    FocusIndicator,
    BrandDetails,
  },
  data() {
    return {
      tableFields: [
        {
          label: this.$t('brand.list.name'),
          key: 'name',
          sortable: true,
        },
        {
          label: this.$t('brand.list.phone'),
          key: 'phone',
          tdClass: 'text-left',
        },
        {
          label: this.$t('brand.list.contacts'),
          key: 'contacts',
          thClass: 'border-left',
          tdClass: 'text-left',
        },
        {
          label: this.$t('status'),
          key: 'status',
          sortable: true,
          tdClass: 'text-center',
        },
        {
          label: this.$t('brand.list.owner'),
          key: 'owner',
          tdClass: 'text-left',
        },
        {
          label: '',
          key: '_details',
          class: 'text-left',
          tdClass: 'align-middle',
          tdAttr: { colspan: '100%' },
        },
      ],
      clickState: { clicks: 0, timer: null },
      items: [],
      currentPage: 1,
      currentPageCount: null,
      perPage: 10,
      totalRows: 0,
      sortBy: 'name',
      sortDir: false,
      loading: false,
      owners: [],
      error: null,
      selectedStatuses: [],
      activeStatuses: [
        AccountStatusEnum.ACTIVE,
        AccountStatusEnum.BEGINNER_ONBOARDING,
        AccountStatusEnum.REPEATED_ONBOARDING,
      ],
      accountStatusOptions: Object.values(AccountStatusEnum).map((s) => ({
        text: s,
        value: s,
      })),
    };
  },
  computed: {
    ...mapFilters({
      moduleName: 'brands',
      states: ['search', 'owner'],
      setMutation: SET_BRANDS_FILTER,
      setCallback: 'noop',
    }),
    noop() {
      return null;
    },
    statusOptions() {
      return Object.values(AccountStatusEnum).map((value) => ({
        value,
        text: value,
      }));
    },
    isActiveStatusesSelected() {
      if (this.selectedStatuses.length !== this.activeStatuses.length)
        return false;
      const selected = this.selectedStatuses.map((s) => s.value).sort();
      const active = [...this.activeStatuses].sort();
      return JSON.stringify(selected) === JSON.stringify(active);
    },
    ownerOptions() {
      return this.owners;
    },
    websiteURL() {
      return (website) => {
        if (website.startsWith('http://') || website.startsWith('https://')) {
          return website;
        }
        return `https://${website}`;
      };
    },
  },
  watch: {
    currentPage() {
      clearTimeout(this.$_timeout);
      this.$_timeout = setTimeout(() => {
        this.loadItems();
      }, 250);
    },
    sortBy() {
      this.loadItems();
    },
    sortDir() {
      this.loadItems();
    },
    filterSearch(nextVal, prevVal) {
      if (
        (normalizeSearchInput(prevVal) !== normalizeSearchInput(nextVal) &&
          nextVal.length > 1) ||
        nextVal.length == 0
      ) {
        this.filterUpdated();
      }
    },
    filterOwner(nextVal, prevVal) {
      if (prevVal !== nextVal) {
        this.filterUpdated();
      }
    },
  },
  created() {
    this.$_timeout = null;
    this.loadItems();

    const gidAccountId = 1;
    const queryString = RequestQueryBuilder.create({
      filter: [
        {
          field: 'accountId',
          operator: CondOperator.EQUALS,
          value: gidAccountId,
        },
      ],
      fields: ['id', 'name', 'isDeleted', 'profiles'],
      sort: [{ field: 'name', order: 'ASC' }],
    }).query();

    axios.get(`/data-api/contact?${queryString}`).then((response) => {
      if (response.data.length) {
        this.owners = response.data
          .filter((record) => !record.isDeleted)
          .filter((record) =>
            record.profiles.some(
              ({ profile }) => profile === ContactProfileEnum.BRAND_MANAGEMENT,
            ),
          )
          .map(({ id, name }) => ({
            value: id,
            text: name,
          }));
      }
    });
  },
  beforeDestroy() {
    clearTimeout(this.$_timeout);
  },
  methods: {
    ...mapMutations([SET_BRANDS_DATA]),
    extractBrandName(cardElement) {
      const nameCell = cardElement.querySelector('td:first-child');
      if (!nameCell) return null;
      const nameParagraph = nameCell.querySelector('p');
      if (!nameParagraph) return null;
      const fullText = nameParagraph.textContent?.trim() || '';
      const firstLine = fullText.split('\n')[0]?.trim();
      return firstLine || null;
    },
    selectActiveStatuses() {
      this.selectedStatuses = this.activeStatuses.map((s) => ({
        text: s,
        value: s,
      }));
      this.filterUpdated();
    },
    clearAllStatuses() {
      this.selectedStatuses = [];
      this.filterUpdated();
    },
    onStatusChange() {
      this.filterUpdated();
    },
    handleClickableText(textToCopy, urlToOpen) {
      this.clickState.clicks++;
      if (this.clickState.clicks === 1) {
        this.clickState.timer = setTimeout(() => {
          this.copyToClipboard(textToCopy);
          this.clickState.clicks = 0;
        }, 200);
      } else {
        clearTimeout(this.clickState.timer);
        window.open(urlToOpen, '_blank');
        this.clickState.clicks = 0;
      }
    },
    handleBrandClick(item) {
      this.handleClickableText(item.name, `./data-ui/Account/${item.id}`);
    },
    handleContactClick(contact) {
      const name = `${contact.firstName} ${contact.lastName}`;
      this.handleClickableText(name, `./data-ui/Contact/${contact.id}`);
    },
    handleActionClick(textToCopy, actionUrl) {
      this.clickState.clicks++;
      if (this.clickState.clicks === 1) {
        this.clickState.timer = setTimeout(() => {
          this.copyToClipboard(textToCopy);
          this.clickState.clicks = 0;
        }, 200);
      } else {
        clearTimeout(this.clickState.timer);
        window.location.href = actionUrl;
        this.clickState.clicks = 0;
      }
    },
    async copyToClipboard(text) {
      try {
        if (navigator.clipboard) {
          await navigator.clipboard.writeText(text);
        } else {
          const el = document.createElement('textarea');
          el.value = text;
          document.body.appendChild(el);
          el.select();
          document.execCommand('copy');
          document.body.removeChild(el);
        }
        this.$bvToast.toast(`${text} copied`, {
          variant: 'success',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 1500,
          noCloseButton: true,
        });
      } catch (e) {
        console.error('Copy failed', e);
      }
    },
    statusVariant(status) {
      const variants = {
        Active: 'success',
        Deactivated: 'danger',
        Suspended: 'danger',
        Restricted: 'warning',
        New: 'info',
        'Beginner onboarding': 'primary',
        'Repeated onboarding': 'primary',
        'Documents pending': 'warning',
        'Not interested': 'secondary',
        'Not Qualified': 'secondary',
      };
      return variants[status] || 'light';
    },
    async updateStatus(item, newStatus) {
      try {
        await axios.patch(`/data-api/brand/${item.id}`, {
          status: newStatus,
        });
        item.status = newStatus;
      } catch (error) {
        console.error('Failed to update status', error);
      }
    },
    async updateOwner(item, newOwnerId) {
      try {
        await axios.patch(`/data-api/brand/${item.id}`, {
          ownerid: newOwnerId,
        });
        item.ownerid = newOwnerId;
      } catch (error) {
        console.error('Failed to update owner', error);
      }
    },
    filterUpdated() {
      this.currentPage = 1;
      this.$nextTick(() => this.loadItems());
    },
    async loadItems() {
      this.loading = true;

      const queryParams = {
        page: this.currentPage,
        limit: this.perPage,
        sort: [
          {
            field: this.sortBy || 'name',
            order: this.sortDir ? 'DESC' : 'ASC',
          },
        ],
        join: [
          {
            field: 'contacts',
            select: [
              'id',
              'firstName',
              'lastName',
              'email',
              'phone',
              'mobilePhone',
            ],
          },
        ],
      };

      const filters = [];

      const searchTerm = normalizeSearchInput(this.filterSearch);
      if (searchTerm && searchTerm.length > 1) {
        filters.push({
          field: 'name',
          operator: CondOperator.CONTAINS_LOW,
          value: searchTerm,
        });
      }

      if (this.filterOwner) {
        filters.push({
          field: 'ownerid',
          operator: CondOperator.EQUALS,
          value: this.filterOwner,
        });
      }

      if (this.selectedStatuses.length > 0) {
        filters.push({
          field: 'status',
          operator: CondOperator.IN,
          value: this.selectedStatuses.map((s) => s.value).join(','),
        });
      }

      if (filters.length) {
        queryParams.filter = filters;
      }

      const queryString = RequestQueryBuilder.create(queryParams).query();

      try {
        const response = await axios.get(`/data-api/brand?${queryString}`);
        const { data, total } = response.data;
        this.items = data;
        this.totalRows = total;
        this.currentPageCount = data.length;
        this.error = null;

        this[SET_BRANDS_DATA](data);
      } catch (error) {
        this.handleError(error);
        this.items = [];
      } finally {
        this.loading = false;
      }
    },
    handleError(error) {
      const err = { details: error };
      if (error.response) {
        err.message = error.response.data.msg || error.response.data;
      } else if (error.request) {
        err.message = this.$t('_errors.network.details');
      } else {
        err.message = error.message;
      }
      this.error = err;
    },
  },
};
</script>
<style scoped lang="scss">
.contacts-cell {
  max-height: 120px;
  overflow-y: auto;
  font-size: 0.85rem;
}

.contact-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1px 0;
  white-space: nowrap;

  &:not(:last-child) {
    border-bottom: 1px solid #eee;
  }
}

.contact-name {
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: 4px;
}

.contact-actions {
  display: flex;
  gap: 2px;
  flex-shrink: 0;
}

.contact-action {
  color: #6c757d;
  text-decoration: none;
  line-height: 1;
  cursor: pointer;

  &:hover {
    color: #007bff;
  }

  .material-icons {
    font-size: 1rem;
    vertical-align: middle;
  }
}

.clickable-text {
  color: #212529;
  cursor: pointer;
  transition: color 0.15s ease-in-out;

  &:hover {
    color: #000;
    text-decoration: underline;
  }

  &.address-line {
    color: inherit;
    font-weight: normal;
  }
}

.status-button-group {
  .status-button {
    padding: 0.25rem 0.5rem;
    display: flex;
    align-items: center;

    .material-icons {
      font-size: 1.1rem;
    }
  }
}

.status-select,
.owner-select {
  min-width: 140px;
  font-size: 0.85rem;
}

.status-success {
  background-color: #d4edda;
  color: #155724;
}
.status-danger {
  background-color: #f8d7da;
  color: #721c24;
}
.status-warning {
  background-color: #fff3cd;
  color: #856404;
}
.status-info {
  background-color: #d1ecf1;
  color: #0c5460;
}
.status-primary {
  background-color: #cce5ff;
  color: #004085;
}
.status-secondary {
  background-color: #e2e3e5;
  color: #383d41;
}
</style>
